import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../api/endpoints';
import * as permissions from '../utils/permissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [skipVerify, setSkipVerify] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Keep rememberMe and rememberedEmail for pre-filling the login form
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token && !skipVerify) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);
        } catch (error) {
          // Check if it's a 503 error (system not installed)
          if (error.response?.status === 503 && error.response?.data?.installed === false) {
            // Don't logout, just redirect to install (handled by axios interceptor)
            setLoading(false);
            return;
          }
          console.error('Session token is invalid, logging out.');
          logout();
        }
      }
      setLoading(false);
      setSkipVerify(false);
    };
    verifyToken();
  }, [token, logout, skipVerify]);

  const login = useCallback(async (credentials, rememberMe = true) => {
    try {
      console.log('AuthContext: Making login request with credentials:', credentials);
      const response = await authAPI.login(credentials);
      console.log('AuthContext: Login response received:', response);

      // Handle OTP requirement
      if (response.data.require_otp) {
        return {
          success: true,
          require_otp: true,
          email: response.data.email,
          message: response.data.message
        };
      }

      const { token, user } = response.data;

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedEmail', credentials.email);
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedEmail');
      }

      // Skip verification since we already have the user from login
      setSkipVerify(true);
      setUser(user);
      setToken(token);

      console.log('AuthContext: Login successful, user set:', user);
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      console.error('AuthContext: Error response:', error.response);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed',
      };
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp, rememberMe = true) => {
    try {
      const response = await authAPI.verifyOtp({ email, otp });
      const { token, user } = response.data;

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedEmail', email);
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedEmail');
      }

      setSkipVerify(true);
      setUser(user);
      setToken(token);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Verification failed',
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setToken(token);
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  }, []);

  // Permission helper methods
  const userPermissions = useMemo(() => ({
    canCreateEvents: () => permissions.canCreateEvents(user),
    canEditEvents: () => permissions.canEditEvents(user),
    canCreateUsers: () => permissions.canCreateUsers(user),
    canCreateAdmins: () => permissions.canCreateAdmins(user),
    canDeleteAnyEvent: () => permissions.canDeleteAnyEvent(user),
    canDeleteAnyUser: () => permissions.canDeleteAnyUser(user),
    canSeeAllEvents: () => permissions.canSeeAllEvents(user),
    canSeeAllUsers: () => permissions.canSeeAllUsers(user),
    isSuperAdmin: () => permissions.isSuperAdmin(user),
    isEventAdmin: () => permissions.isEventAdmin(user),
    isAdmin: () => permissions.isAdmin(user),
    getAllowedRolesToCreate: () => permissions.getAllowedRolesToCreate(user),
    canDeleteUser: (targetUser) => permissions.canDeleteUser(user, targetUser),
    canEditUser: (targetUser) => permissions.canEditUser(user, targetUser),
    getNavigationItems: () => permissions.getNavigationItems(user),
  }), [user]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    verifyOtp,
    register,
    logout,
    isAuthenticated: !!token,
    permissions: userPermissions,
  }), [user, token, loading, login, verifyOtp, register, logout, userPermissions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
